import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import PropTypes from "prop-types";
import "./CommunityActions.scss";
import shareIcon from "../Communities/img/share.png";
import copyIcon from "../Communities/img/copy.png";
import notificationsOnIcon from "../Communities/img/notificationsOn.png";
import notificationsOffIcon from "../Communities/img/notificationsOff.png";
import reportIcon from "./img/report.png";
import leaveIcon from "./img/leave.png";
import editIcon from "./img/edit.png";
import blockIcon from "./img/block.png";
import deleteIcon from "./img/delete.png";

import ReportsModal from "../Reports/Reports";
import Alerts from "../Alerts/Alerts";
import { useToast } from "../Alerts/Toast";
import { getAccessToken } from "../../awsAuth";

const API_BASE = process.env.REACT_APP_API_BASE || "http://localhost:5000";

const SwitchRow = ({ title, subtitle, checked, onChange, loading }) => (
  <div
    className={`ca-toggle ${loading ? "is-loading" : ""}`}
    aria-busy={loading ? "true" : "false"}
  >
    <div className="ca-toggle__label">
      <b>{title}</b>
      {subtitle ? <small>{subtitle}</small> : null}
    </div>
    <label className="ca-switch" aria-label={title}>
      <input
        type="checkbox"
        checked={!!checked}
        onChange={(e) => {
          if (loading) return;
          onChange?.(e.target.checked);
        }}
        disabled={loading}
      />
      <span className="ca-switch__slider" />
    </label>

    {loading && (
      <div className="ca-toggle__loader">
        <div className="ca-toggle__spinner" />
      </div>
    )}
  </div>
);

SwitchRow.propTypes = {
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string,
  checked: PropTypes.bool,
  onChange: PropTypes.func,
  loading: PropTypes.bool,
};

const CommunityActions = ({
  onClose,
  isOwner,
  isPrivate,
  mode,
  allowComments,
  onTogglePrivacy,
  onToggleMode,
  onToggleComments,
  onShare,
  onReport,
  onLeave,
  onEdit,
  onShowBlocked,
  onDeleteCommunity,

  communityId,
  userId,
  notificationsEnabled = true,
  onToggleNotifications,
}) => {
  const [isOpen, setIsOpen] = useState(true);
  const [isDeleting, setIsDeleting] = useState(false);
  const [notifEnabled, setNotifEnabled] = useState(!!notificationsEnabled);
  const [isClosing, setIsClosing] = useState(false);

  const toast = useToast();
  const [confirmOpen, setConfirmOpen] = useState(false);

  const [privacyLoading, setPrivacyLoading] = useState(false);
  const [modeLoading, setModeLoading] = useState(false);
  const [commentsLoading, setCommentsLoading] = useState(false);

  const [copySuccess, setCopySuccess] = useState(false);

  const sheetRef = useRef(null);
  const firstFocusRef = useRef(null);

  useEffect(() => setNotifEnabled(!!notificationsEnabled), [notificationsEnabled]);

  const handleRequestClose = useCallback(() => {
    if (isClosing) return;
    setIsClosing(true);
    setIsOpen(false);
    setTimeout(() => onClose?.(), 240);
  }, [isClosing, onClose]);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const scrollY = window.scrollY || window.pageYOffset || 0;

    const originalOverflow = document.body.style.overflow;
    const originalPosition = document.body.style.position;
    const originalTop = document.body.style.top;
    const originalWidth = document.body.style.width;

    const html = document.documentElement;
    const originalHtmlOverflow = html.style.overflow;

    html.style.overflow = "hidden";
    document.body.style.overflow = "hidden";
    document.body.style.position = "fixed";
    document.body.style.top = `-${scrollY}px`;
    document.body.style.width = "100%";

    return () => {
      html.style.overflow = originalHtmlOverflow || "";
      document.body.style.overflow = originalOverflow || "";
      document.body.style.position = originalPosition || "";
      document.body.style.top = originalTop || "";
      document.body.style.width = originalWidth || "";

      window.scrollTo(0, scrollY);
    };
  }, []);

  useEffect(() => {
    const t = setTimeout(() => firstFocusRef.current?.focus(), 10);
    const onKey = (e) => {
      if (e.key === "Escape") handleRequestClose();
    };
    window.addEventListener("keydown", onKey);
    return () => {
      clearTimeout(t);
      window.removeEventListener("keydown", onKey);
    };
  }, [handleRequestClose]);

  const stop = (e) => e.stopPropagation();

  const handleDelete = async () => {
    if (!onDeleteCommunity) {
      toast.error("Внутрішня помилка: onDeleteCommunity не передано у CommunityActions.", 3200);
      return;
    }
    if (isDeleting) return;
    setConfirmOpen(true);
  };

  const runConfirmedDelete = async () => {
    if (!onDeleteCommunity) {
      toast.error("Внутрішня помилка: onDeleteCommunity не передано у CommunityActions.", 3200);
      setConfirmOpen(false);
      return;
    }
    if (isDeleting) return;

    setConfirmOpen(false);

    try {
      setIsDeleting(true);
      await onDeleteCommunity();
      toast.success("Спільноту видалено");
      handleRequestClose();
    } catch (e) {
      console.error("[UI] Delete failed:", e);
      toast.error(String(e?.message || "Не вдалося видалити спільноту"), 3200);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleToggleNotifications = async () => {
    const nextEnabled = !notifEnabled;
    setNotifEnabled(nextEnabled);
    try {
      window.dispatchEvent(
        new CustomEvent("cv:communityNotifToggled", {
          detail: { communityId, enabled: nextEnabled },
        })
      );
    } catch {}

    if (typeof onToggleNotifications === "function") {
      try {
        await onToggleNotifications(nextEnabled);
      } catch {
        setNotifEnabled(!nextEnabled);
      }
      return;
    }

    try {
      if (!communityId || !userId) return;

      const token = await getAccessToken();
      if (!token) throw new Error("Немає токена");

      await fetch(`${API_BASE}/api/communities/${communityId}/notifications`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ enabled: nextEnabled }),
      });
    } catch {
      setNotifEnabled(!nextEnabled);
    }
  };

  const notifIcon = notifEnabled ? notificationsOffIcon : notificationsOnIcon;
  const notifLabel = notifEnabled ? "Вимкнути сповіщення" : "Увімкнути сповіщення";

  const REPORT_REASONS = useMemo(
    () => [
      { key: "spam", label: "Спам" },
      { key: "scam", label: "Шахрайство" },
      { key: "hate", label: "Мова ворожнечі" },
      { key: "nudity", label: "Непристойний контент" },
      { key: "violence", label: "Насильство" },
      { key: "illegal", label: "Незаконний контент" },
      { key: "misinformation", label: "Дезінформація" },
      { key: "other", label: "Інше" },
    ],
    []
  );

  const [reportOpen, setReportOpen] = useState(false);
  const [reportReason, setReportReason] = useState("spam");
  const [reportDetails, setReportDetails] = useState("");
  const [reportSending, setReportSending] = useState(false);
  const [reportErr, setReportErr] = useState("");
  const [reportDone, setReportDone] = useState(false);

  const openReport = useCallback(() => {
    if (!userId) {
      const next =
        typeof window !== "undefined"
          ? encodeURIComponent(window.location.pathname + window.location.search)
          : "";
      const ok = window.confirm(
        "Щоб поскаржитись, увійдіть у свій акаунт. Перейти до авторизації?"
      );
      if (ok && typeof window !== "undefined") window.location.href = `/auth?next=${next}`;
      return;
    }
    setReportErr("");
    setReportDone(false);
    setReportReason("spam");
    setReportDetails("");
    setReportOpen(true);
  }, [userId]);

  const submitReport = useCallback(async () => {
    if (!userId) {
      setReportErr("Потрібно увійти в акаунт");
      return;
    }
    if (!communityId) {
      setReportErr("Невірна спільнота");
      return;
    }
    setReportSending(true);
    setReportErr("");
    try {
      const token = await getAccessToken();
      if (!token) {
        setReportErr("Авторизація потрібна для подання скарги.");
        return;
      }

      const body = {
        targetType: "group",
        targetId: String(communityId),
        reason: reportReason,
        details: reportDetails?.trim() || undefined,
      };

      const res = await fetch(`${API_BASE}/api/reports`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(body),
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data?.ok) {
        if (
          res.status === 409 &&
          (data?.error === "ALREADY_REPORTED" || data?.error === "DUPLICATE_WRITE")
        ) {
          setReportErr("Ви вже подавали скаргу на цю спільноту.");
        } else if (res.status === 401) {
          setReportErr("Авторизація потрібна для подання скарги.");
        } else {
          setReportErr(data?.error || "Не вдалося надіслати скаргу");
        }
        return;
      }
      setReportDone(true);
      setTimeout(() => {
        setReportOpen(false);
        handleRequestClose();
      }, 900);
    } catch {
      setReportErr("Сталася помилка. Спробуйте пізніше.");
    } finally {
      setReportSending(false);
    }
  }, [communityId, userId, reportReason, reportDetails, handleRequestClose]);

  const handlePrivacyChange = useCallback(
    async (v) => {
      if (!onTogglePrivacy || privacyLoading) return;
      setPrivacyLoading(true);
      try {
        const res = onTogglePrivacy(v);
        if (res && typeof res.then === "function") {
          await res;
        }
      } finally {
        setPrivacyLoading(false);
      }
    },
    [onTogglePrivacy, privacyLoading]
  );

  const handleModeChange = useCallback(
    async (v) => {
      if (!onToggleMode || modeLoading) return;
      const nextMode = v ? "channel" : "community";
      setModeLoading(true);
      try {
        const res = onToggleMode(nextMode);
        if (res && typeof res.then === "function") {
          await res;
        }
      } finally {
        setModeLoading(false);
      }
    },
    [onToggleMode, modeLoading]
  );

  const handleCommentsChange = useCallback(
    async (v) => {
      if (!onToggleComments || commentsLoading) return;
      setCommentsLoading(true);
      try {
        const res = onToggleComments(v);
        if (res && typeof res.then === "function") {
          await res;
        }
      } finally {
        setCommentsLoading(false);
      }
    },
    [onToggleComments, commentsLoading]
  );

  const getCommunityUrl = useCallback(() => {
    if (typeof window === "undefined") return "";
    return window.location.href;
  }, []);

  const handleCopyLink = useCallback(async () => {
    try {
      const url = getCommunityUrl();
      await navigator.clipboard.writeText(url);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 1800);
    } catch (err) {
      console.error("Не вдалося скопіювати посилання:", err);
      toast.error("Не вдалося скопіювати посилання", 3200);
    }
  }, [getCommunityUrl]);

  const handleNativeShare = useCallback(async () => {
    try {
      const url = getCommunityUrl();
      const title = "Спільнота Cvanto";
      const text = "Подивись цю спільноту у Cvanto";

      if (navigator.share) {
        await navigator.share({ title, text, url });
        return;
      }

      await handleCopyLink();
      toast.success("Посилання скопійовано (шеринг не підтримується на цьому пристрої).", 2600);
    } catch (err) {
      console.error("Не вдалося поділитися спільнотою:", err);
      toast.error("Не вдалося поділитися спільнотою", 3200);
    }
  }, [getCommunityUrl, handleCopyLink]);

  return (
    <div
      className={`ca-overlay ${isOpen ? "is-open" : ""} ${isClosing ? "is-closing" : ""}`}
      onClick={handleRequestClose}
      role="dialog"
      aria-modal="true"
      aria-label="Дії спільноти"
    >
      <div
        className={`ca-modal ${isOpen ? "is-open" : ""}`}
        onClick={stop}
        role="document"
        ref={sheetRef}
      >
        {isOwner ? (
          <>
            <SwitchRow
              title={isPrivate ? "Закрита спільнота" : "Відкрита спільнота"}
              subtitle={
                isPrivate
                  ? "Учасники подають заявки. Контент приховано до схвалення."
                  : "Будь-хто може переглядати і публікувати."
              }
              checked={!!isPrivate}
              loading={privacyLoading}
              onChange={handlePrivacyChange}
            />
            <SwitchRow
              title={mode === "channel" ? "Режим: Канал" : "Режим: Спільнота"}
              subtitle={
                mode === "channel"
                  ? "Пости може створювати лише власник. Інші — тільки читають."
                  : "Усі учасники можуть публікувати."
              }
              checked={mode === "channel"}
              loading={modeLoading}
              onChange={handleModeChange}
            />
            <SwitchRow
              title={allowComments ? "Коментарі: увімкнено" : "Коментарі: вимкнено"}
              subtitle="Впливає на всі пости у спільноті"
              checked={!!allowComments}
              loading={commentsLoading}
              onChange={handleCommentsChange}
            />

            <button ref={firstFocusRef} onClick={onEdit} className="ca-btn">
              <img src={editIcon} alt="Редагувати" className="no-invert" />
              Редагувати спільноту
            </button>

            <button className="ca-btn" onClick={onShowBlocked}>
              <img src={blockIcon} alt="Заблоковані" className="no-invert" />
              Заблоковані учасники
            </button>

            <button
              className="ca-btn danger"
              onClick={handleDelete}
              disabled={isDeleting}
              aria-busy={isDeleting ? "true" : "false"}
            >
              <img src={deleteIcon} alt="Видалити" className="no-invert" />
              {isDeleting ? "Видаляємо..." : "Видалити спільноту"}
            </button>
          </>
        ) : (
          <>
            <button className="ca-btn ca-btn--mobile-only" onClick={handleNativeShare}>
              <img src={shareIcon} alt="Поділитись" />
              Поділитись спільнотою
            </button>

            <button className="ca-btn ca-btn--desktop-only" onClick={handleCopyLink}>
              <img src={copyIcon} alt="Копіювати посилання" />
              {copySuccess ? "Скопійовано!" : "Копіювати посилання"}
            </button>

            <button className="ca-btn ca-btn--mobile-only" onClick={handleCopyLink}>
              <img src={copyIcon} alt="Копіювати посилання" />
              {copySuccess ? "Скопійовано!" : "Копіювати посилання"}
            </button>

            <button
              className={`ca-btn ca-bell ${notifEnabled ? "is-on" : "is-off"}`}
              onClick={handleToggleNotifications}
              aria-pressed={notifEnabled ? "true" : "false"}
              aria-label={notifLabel}
            >
              <img src={notifIcon} alt="Сповіщення" />
              {notifEnabled ? "Вимкнути сповіщення" : "Увімкнути сповіщення"}
            </button>

            <button className="ca-btn" onClick={openReport}>
              <img src={reportIcon} alt="Поскаржитись" />
              Поскаржитись на спільноту
            </button>

            <button className="ca-btn" onClick={onLeave}>
              <img src={leaveIcon} alt="Покинути" />
              Покинути спільноту
            </button>
          </>
        )}
      </div>

      <ReportsModal
        open={reportOpen}
        onClose={() => setReportOpen(false)}
        onSubmitted={() => {
          try {
            handleRequestClose();
          } catch {}
        }}
        userId={userId}
        targetType="group"
        targetId={communityId}
        reasons={REPORT_REASONS}
        defaultReason="spam"
        defaultDetails=""
      />

      <Alerts
        open={confirmOpen}
        variant="deleteListing"
        title="Видалити спільноту?"
        text="Цю дію неможливо відмінити."
        primaryText="Видалити"
        secondaryText={null}
        cancelText="Скасувати"
        tone="danger"
        onCancel={() => setConfirmOpen(false)}
        onClose={() => setConfirmOpen(false)}
        onPrimary={runConfirmedDelete}
      />
    </div>
  );
};

CommunityActions.propTypes = {
  onClose: PropTypes.func,
  isOwner: PropTypes.bool.isRequired,
  isPrivate: PropTypes.bool,
  mode: PropTypes.oneOf(["community", "channel"]),
  allowComments: PropTypes.bool,
  onTogglePrivacy: PropTypes.func,
  onToggleMode: PropTypes.func,
  onToggleComments: PropTypes.func,
  onShare: PropTypes.func,
  onReport: PropTypes.func,
  onLeave: PropTypes.func,
  onEdit: PropTypes.func,
  onShowBlocked: PropTypes.func,
  onDeleteCommunity: PropTypes.func,
  communityId: PropTypes.string.isRequired,
  userId: PropTypes.string,
  notificationsEnabled: PropTypes.bool,
  onToggleNotifications: PropTypes.func,
};

export default CommunityActions;